import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, MessageCircle, Star } from "lucide-react";
import { TESTIMONIALS, HOME_REVIEWS } from "../data/testimonials";
import { Reveal, Chapter } from "./Reveal";
import { openLeadPopup } from "./Navbar";
import { waLink, CONTEXT_MESSAGES } from "../lib/whatsapp";
import { track } from "../lib/analytics";

const Stars = ({ count = 5, className = "h-3.5 w-3.5" }) => (
  <div className="flex gap-0.5 text-ember" aria-label={`${count} star rating`}>
    {Array.from({ length: count }).map((_, i) => (
      <Star key={i} className={className} fill="currentColor" />
    ))}
  </div>
);

const Card = ({ t, index }) => (
  <article
    data-testid={`review-card-${index}`}
    className="flex w-[300px] shrink-0 flex-col rounded-2xl border border-obsidian/10 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-obsidian/10 sm:w-[380px] sm:p-7"
  >
    <div className="flex items-center justify-between">
      <Stars count={t.rating} />
      <span className="rounded-full border border-obsidian/10 px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.15em] text-obsidian/40">
        {t.country}
      </span>
    </div>
    <p className="mt-4 font-display text-sm font-bold tracking-tight text-obsidian">"{t.title}"</p>
    <p className="mt-2 flex-1 text-sm leading-relaxed text-obsidian/70">{t.quote}</p>
    <div className="mt-6 flex items-center gap-3 border-t border-obsidian/10 pt-4">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-obsidian font-display text-xs font-bold tracking-wide text-ember">
        {t.initials}
      </span>
      <div className="min-w-0">
        <p className="truncate font-display text-sm font-bold text-obsidian">{t.name}</p>
        <p className="truncate text-xs text-obsidian/50">
          {t.designation} · {t.location}
        </p>
      </div>
    </div>
  </article>
);

export default function Testimonials() {
  const row1 = HOME_REVIEWS.slice(0, 4);
  const row2 = HOME_REVIEWS.slice(4, 8);

  return (
    <section
      className="relative overflow-hidden bg-bone py-20 text-obsidian lg:py-32"
      data-testid="reviews-section"
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.035]"
        style={{
          backgroundImage:
            "linear-gradient(#0A0A0B 1px, transparent 1px), linear-gradient(90deg, #0A0A0B 1px, transparent 1px)",
          backgroundSize: "56px 56px",
        }}
        aria-hidden="true"
      />
      <div className="pointer-events-none absolute -top-40 left-1/2 h-96 w-[42rem] -translate-x-1/2 rounded-full bg-ember/10 blur-3xl" aria-hidden="true" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
        <Chapter index="09" label="What Businesses Say" dark />
        <div className="mt-6 flex flex-wrap items-end justify-between gap-8">
          <Reveal delay={0.05}>
            <h2 className="max-w-xl font-display text-3xl font-bold leading-[1.05] tracking-tight sm:text-4xl lg:text-5xl">
              Client Words. <span className="text-obsidian/35">Carry Weight.</span>
            </h2>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-obsidian/55 sm:text-base">
              Businesses choose Grotech Web for websites that don't just look premium — they are
              designed to build trust, generate enquiries and help businesses grow online.
            </p>
            <p className="mt-3 max-w-xl text-xs text-obsidian/40">
              Client testimonials reflect feedback provided to Grotech Web. Individual experiences may vary.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="text-left sm:text-right">
              <p className="font-display text-5xl font-black tracking-tight text-obsidian">{TESTIMONIALS.length}</p>
              <p className="mt-1 text-sm text-obsidian/50">client testimonials from India & the USA</p>
              <Link
                to="/reviews/"
                data-testid="read-all-reviews-link"
                className="group mt-3 inline-flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-ember"
              >
                Read All Reviews
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </Link>
            </div>
          </Reveal>
        </div>
      </div>

      {/* Desktop: dual counter-scrolling marquee rows */}
      <div className="relative mt-14 hidden md:block">
        <Reveal delay={0.2}>
          <div className="marquee-row relative overflow-hidden py-2">
            <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-24 bg-gradient-to-r from-bone to-transparent" />
            <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-24 bg-gradient-to-l from-bone to-transparent" />
            <div className="marquee-track-left flex w-max gap-5">
              {[...row1, ...row1].map((t, i) => (
                <Card key={`r1-${i}`} t={t} index={i % row1.length} />
              ))}
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.32}>
          <div className="marquee-row relative mt-5 overflow-hidden py-2">
            <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-24 bg-gradient-to-r from-bone to-transparent" />
            <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-24 bg-gradient-to-l from-bone to-transparent" />
            <div className="marquee-track-right flex w-max gap-5">
              {[...row2, ...row2].map((t, i) => (
                <Card key={`r2-${i}`} t={t} index={5 + (i % row2.length)} />
              ))}
            </div>
          </div>
        </Reveal>
      </div>

      {/* Mobile: swipeable snap carousel */}
      <div className="mt-12 md:hidden">
        <Reveal delay={0.2}>
          <div
            className="flex snap-x snap-mandatory gap-4 overflow-x-auto px-4 pb-4 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            data-testid="reviews-mobile-carousel"
          >
            {HOME_REVIEWS.map((t, i) => (
              <div key={i} className="w-[85%] shrink-0 snap-center first:ml-0">
                <Card t={t} index={i} />
              </div>
            ))}
          </div>
        </Reveal>
      </div>

      <Reveal delay={0.15}>
        <div className="relative mx-auto mt-16 max-w-3xl px-4 text-center sm:px-8">
          <h3 className="font-display text-2xl font-bold leading-tight tracking-tight sm:text-3xl">
            Ready to Build a Website That Brings You Business?
          </h3>
          <p className="mx-auto mt-3 max-w-md text-sm text-obsidian/55">
            Let's create a professional online presence designed around your business goals.
          </p>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-4">
            <button
              data-testid="reviews-cta-btn"
              onClick={() => { track("cta_click", { location: "reviews" }); openLeadPopup(); }}
              className="group flex items-center gap-2 rounded-full bg-ember px-7 py-4 font-display text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-ember-hover"
            >
              Get My Website Plan
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
            <a
              href={waLink(CONTEXT_MESSAGES.reviews)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="reviews-whatsapp-btn"
              onClick={() => track("whatsapp_click", { location: "reviews" })}
              className="flex items-center gap-2 rounded-full border border-[#25D366]/60 px-7 py-4 font-display text-sm font-bold uppercase tracking-wider text-[#1da851] transition-colors hover:bg-[#25D366]/10"
            >
              <MessageCircle className="h-4 w-4" /> Chat on WhatsApp
            </a>
          </div>
        </div>
      </Reveal>
    </section>
  );
}
